import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userManager } from '../../config/oidc';

export interface OrganizationStatsDto {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  userCount: number;
}

interface OrganizationStatsState {
  data: OrganizationStatsDto | null;
  loading: boolean;
  error: string | null;
  sessionExpired: boolean;
}

const initialState: OrganizationStatsState = {
  data: null,
  loading: false,
  error: null,
  sessionExpired: false,
};

export const fetchOrganizationStats = createAsyncThunk(
  'organizationStats/fetchOrganizationStats',
  async (orgId: string, { rejectWithValue }) => {
    try {
      const apiUrl = import.meta.env.VITE_API_ORGADMIN_API_BASE || 'https://test-api.sirktek.com/orgadmin/v1';
      const statsUrl = `${apiUrl}/${orgId}/stats`;

      // Get access token from OIDC user manager
      const user = await userManager.getUser();
      const token = user?.access_token;
      if (!token) {
        return rejectWithValue({ message: 'Not authenticated', sessionExpired: true });
      }

      const response = await fetch(statsUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json = await response.json();
      return json as OrganizationStatsDto;
    } catch (err) {
      if (
        err instanceof Error &&
        (
          err.message.includes('login_required') ||
          err.message.includes('invalid_grant') ||
          err.message.includes('consent_required') ||
          err.message.includes('unauthorized') ||
          err.message.includes('401') ||
          err.message.includes('403')
        )
      ) {
        return rejectWithValue({
          message: err.message,
          sessionExpired: true
        });
      }

      return rejectWithValue({
        message: err instanceof Error ? err.message : 'Unknown error',
        sessionExpired: false
      });
    }
  }
);

const organizationStatsSlice = createSlice({
  name: 'organizationStats',
  initialState,
  reducers: {
    clearOrganizationStatsError: (state) => {
      state.error = null;
    },
    resetStatsSessionExpired: (state) => {
      state.sessionExpired = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationStats.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.sessionExpired = false;
      })
      .addCase(fetchOrganizationStats.fulfilled, (state, action: PayloadAction<OrganizationStatsDto>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchOrganizationStats.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const payload = action.payload as { message: string, sessionExpired: boolean };
          state.error = payload.message;
          state.sessionExpired = payload.sessionExpired;
        } else {
          state.error = action.error.message || 'Unknown error';
        }
      });
  },
});

export const { clearOrganizationStatsError, resetStatsSessionExpired } = organizationStatsSlice.actions;
export default organizationStatsSlice.reducer;